// @ts-check
import clsx from 'clsx'
import InlineLoader from '../InlineLoader'

/** @typedef {{ isOpen?: boolean, title?: React.ReactNode, body?: React.ReactNode, onClose?: () => void, primaryLabel?: React.ReactNode, onPrimary?: () => void, primaryLoading?: boolean, primaryClassName?: string, secondaryLabel?: React.ReactNode, onSecondary?: () => void, secondaryLoading?: boolean, secondaryClassName?: string }} Props */
/** @param {Props} props */
export default function Modal({
  isOpen = false,
  title,
  body,
  onClose,
  primaryLabel,
  onPrimary,
  primaryLoading = false,
  primaryClassName = 'mdc-button mdc-button--primary mdc-button--size-md',
  secondaryLabel,
  onSecondary,
  secondaryLoading = false,
  secondaryClassName = 'mdc-button mdc-button--secondary mdc-button--size-md',
}) {
  return (
    <div
      className={clsx('mdc-modal', { 'mdc-modal--open': isOpen })}
      role="dialog"
      aria-modal="true"
    >
      <div className="mdc-modal__overlay"></div>
      <div className="mdc-modal__content">
        <div className="mdc-modal__header">
          <div
            className={`mdc-modal-header ${title ? 'mdc-modal-header--type-back-plus-heading-plus-close' : 'mdc-modal-header--type-logo-plus-close'}`}
          >
            {title ? (
              // Need the margin-bottom override for foundation. Can remove once foundation is gone.
              <div className="mdc-modal-header__heading">
                <p style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem', marginBottom: '0' }}>
                  {title}
                </p>
              </div>
            ) : (
              <div className="mdc-modal-header__logo">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="146"
                  height="33"
                  viewBox="0 0 146 33"
                  fill="none"
                  aria-hidden="true"
                  className="brand-logo brand-logo--size-xs brand-logo--color-primary"
                >
                  <path
                    d="M33.2442 31.5694H27.2233V17.5514C27.2233 16.0434 26.8449 14.8522 26.0892 13.9815C25.4491 13.1982 24.6346 12.806 23.6458 12.806C22.5991 12.806 21.6969 13.2275 20.9411 14.069C20.1264 14.9979 19.7194 16.2171 19.7194 17.7265V31.5694H13.5679V17.6389C13.5679 16.0714 13.3788 14.9543 13.0009 14.2863C12.4484 13.3876 11.4152 12.9372 9.90376 12.9372C7.40201 12.9372 6.15152 14.4466 6.15152 17.4642V31.5694H1.33577e-06V8.36582H5.80277V10.5863C5.80277 11.4288 5.81679 11.8491 5.84619 11.8491C5.84619 11.8199 5.86054 11.7908 5.8899 11.7617C6.79096 10.3689 7.70735 9.36699 8.6381 8.75791C9.68533 8.09088 10.9942 7.7564 12.5647 7.7564C14.1355 7.7564 15.3932 8.03985 16.3387 8.60565C17.2831 9.17162 18.1776 10.1369 19.0216 11.5004C19.8647 10.2536 20.7734 9.30196 21.748 8.64893C22.7222 7.99607 23.9079 7.66946 25.3038 7.66946C26.7872 7.66946 28.2049 8.0471 29.5575 8.80123C30.9101 9.55646 31.8623 10.536 32.4148 11.7405C32.9674 12.9451 33.2442 14.2 33.2442 15.5054V31.5694Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M52.3508 19.99C52.3508 15.288 50.4892 12.9372 46.7665 12.9372C43.0146 12.9372 41.1387 15.288 41.1387 19.99C41.1387 24.6917 43.0146 27.0425 46.7665 27.0425C50.4892 27.0425 52.3508 24.6917 52.3508 19.99ZM58.5889 19.99C58.5889 23.6757 57.5276 26.628 55.4046 28.8488C53.2812 31.0685 50.4022 32.1794 46.7665 32.1794C43.0725 32.1794 40.1573 31.0542 38.0193 28.805C35.8818 26.5564 34.8128 23.6181 34.8128 19.99C34.8128 16.3325 35.8745 13.3799 37.9973 11.1307C40.1201 8.8819 43.0433 7.75641 46.7665 7.75641C50.4892 7.75641 53.4121 8.90327 55.5356 11.1958C57.5707 13.4313 58.5889 16.3614 58.5889 19.99Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M76.4319 20.5121C76.4319 15.5201 74.7016 13.0245 71.2403 13.0245C67.7507 13.0245 66.0056 15.4047 66.0056 20.1636C66.0056 22.3991 66.5432 24.1682 67.6197 25.475C68.5793 26.6359 69.7719 27.2161 71.1968 27.2161C72.6217 27.2161 73.8143 26.6503 74.7747 25.5184C75.8795 24.2703 76.4319 22.6019 76.4319 20.5121ZM82.322 31.5694H76.3449V28.6964H76.2583C74.8038 31.0764 72.6661 32.2666 69.8454 32.2666C67.8668 32.2666 66.0483 31.6929 64.3911 30.5468C62.7331 29.4003 61.5409 27.9276 60.8136 26.1285C60.0864 24.3288 59.7232 22.2098 59.7232 19.7713C59.7232 16.4635 60.4787 13.7203 61.9915 11.5444C63.7069 9.01915 66.1649 7.75641 69.3648 7.75641C72.2146 7.75641 74.4542 8.97553 76.083 11.4132H76.17V0.268778H82.322V31.5694Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M99.5961 17.7265C99.45 16.1721 98.92 14.9497 98.0035 14.0576C97.0875 13.1656 96.033 12.7198 94.8408 12.7198C93.6769 12.7198 92.6739 13.1366 91.8305 13.9714C90.8997 14.8632 90.3177 16.115 90.0855 17.7265H99.5961ZM105.879 21.6002H89.8675C90.0124 23.5164 90.6235 24.9966 91.7 26.0408C92.6308 26.9116 93.7647 27.3462 95.1026 27.3462C96.0909 27.3462 96.9786 27.1144 97.7641 26.6503C98.5488 26.1865 99.0577 25.5621 99.2908 24.7782H105.573C104.875 27.1289 103.624 28.9507 101.821 30.2418C100.018 31.5337 97.8794 32.1794 95.4084 32.1794C87.6423 32.1794 83.7599 27.9556 83.7599 19.5104C83.7599 15.9413 84.7414 13.0895 86.7048 10.956C88.6677 8.82294 91.4225 7.75641 94.9721 7.75641C102.242 7.75641 105.879 12.3714 105.879 21.6002Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M113.774 31.5694H107.623V0.268778H113.774V31.5694Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M136.896 24.1682C136.896 25.8235 136.488 27.2601 135.674 28.4791C133.929 31.0035 130.904 32.2666 126.599 32.2666C123.66 32.2666 121.146 31.5979 119.052 30.2632C116.724 28.755 115.547 26.7231 115.518 24.1682H121.844C121.844 25.3013 122.338 26.1865 123.327 26.824C124.2 27.3766 125.275 27.6512 126.556 27.6512C127.69 27.6512 128.672 27.4268 129.5 26.9766C130.33 26.5272 130.744 25.9399 130.744 25.214C130.744 24.2558 129.507 23.502 127.035 22.9506C123.371 22.1089 121.059 21.4559 120.099 20.9908C117.626 19.8013 116.391 17.9729 116.391 15.5054C116.391 14.0543 116.739 12.7344 117.438 11.5444C118.136 10.3544 119.314 9.40404 120.971 8.69255C122.63 7.98141 124.418 7.62585 126.338 7.62585C128.955 7.62585 131.151 8.25057 132.925 9.49822C134.961 10.891 136.051 12.8655 136.197 15.418H130.133C129.812 13.3585 128.445 12.3275 126.032 12.3275C125.043 12.3275 124.185 12.5451 123.459 12.9808C122.731 13.4156 122.368 13.9681 122.368 14.6348C122.368 15.5054 123.618 16.2171 126.119 16.7682C129.755 17.5808 132.096 18.2343 133.143 18.7273C135.645 19.9463 136.896 21.7602 136.896 24.1682Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M145.097 31.4822H138.508V25.1263H145.097V31.4822Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </div>
            )}
            {onClose ? (
              <div className="mdc-modal-header__close">
                <button
                  className="mdc-icon-button mdc-icon-button--tertiary mdc-icon-button--size-lg mdc-icon-button--icon-size-default"
                  aria-label="Close"
                  onClick={onClose}
                >
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M19.2807 18.2194C19.3504 18.2891 19.4056 18.3718 19.4433 18.4629C19.4811 18.5539 19.5005 18.6515 19.5005 18.7501C19.5005 18.8486 19.4811 18.9462 19.4433 19.0372C19.4056 19.1283 19.3504 19.211 19.2807 19.2807C19.211 19.3504 19.1283 19.4056 19.0372 19.4433C18.9462 19.4811 18.8486 19.5005 18.7501 19.5005C18.6515 19.5005 18.5539 19.4811 18.4629 19.4433C18.3718 19.4056 18.2891 19.3504 18.2194 19.2807L12.0001 13.0604L5.78068 19.2807C5.63995 19.4214 5.44907 19.5005 5.25005 19.5005C5.05103 19.5005 4.86016 19.4214 4.71943 19.2807C4.5787 19.1399 4.49963 18.9491 4.49963 18.7501C4.49963 18.551 4.5787 18.3602 4.71943 18.2194L10.9397 12.0001L4.71943 5.78068C4.5787 5.63995 4.49963 5.44907 4.49963 5.25005C4.49963 5.05103 4.5787 4.86016 4.71943 4.71943C4.86016 4.5787 5.05103 4.49963 5.25005 4.49963C5.44907 4.49963 5.63995 4.5787 5.78068 4.71943L12.0001 10.9397L18.2194 4.71943C18.3602 4.5787 18.551 4.49963 18.7501 4.49963C18.9491 4.49963 19.1399 4.5787 19.2807 4.71943C19.4214 4.86016 19.5005 5.05103 19.5005 5.25005C19.5005 5.44907 19.4214 5.63995 19.2807 5.78068L13.0604 12.0001L19.2807 18.2194Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </button>
              </div>
            ) : null}
          </div>
          <div className="mdc-modal__body">{body}</div>
          <div className="mdc-modal__cta">
            <div className="mdc-button-group mdc-button-group--size-md mdc-button-group--display-stacked">
              <button className={primaryClassName} disabled={primaryLoading} onClick={onPrimary}>
                <span className="mdc-button__label">
                  {primaryLoading ? <InlineLoader /> : primaryLabel}
                </span>
              </button>
              {secondaryLabel && onSecondary ? (
                <button
                  className={secondaryClassName}
                  disabled={secondaryLoading}
                  onClick={onSecondary}
                >
                  <span className="mdc-button__label">
                    {secondaryLoading ? <InlineLoader /> : secondaryLabel}
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
